import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { GiftIcon } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { giftColumns } from "./tableParams/giftColumns";
import { sendGift } from "../../api/legacy/GiftAPI";
import OfferAPI from "../../api/OfferAPI";
import useAbility from "../../hooks/useAbility";
import { getElementByUuid } from "../../utils/data/Content";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { giftEditPath } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { Gift } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";
import type {
  GiftStatus,
  TransformedGiftItem,
} from "./tableParams/giftColumns.types";

function getStatus(item: Gift): GiftStatus {
  const today = moment();
  const limitDate = moment(item.limit_date);
  const beginDate = moment(item.start_date);
  const endDate = moment(item.start_date).add(item.month_duration, "months");

  if (item.subscription) {
    return today.isBetween(beginDate, endDate) ? "active" : "finished";
  }
  return today.isAfter(limitDate) ? "expired" : "waiting";
}

function GiftList(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-gift");
  const dispatch = useDispatch();
  const history = useHistory();

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const offers = useSelector((state: AppState) => state.fetchReducer.offers);

  const giftListQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getGiftList,
    params: [{ projectSlug }],
  });

  const {
    data: giftResponse,
    isLoading: isGiftsLoading,
    isError: isGiftsError,
    refetch: refetchGifts,
  } = useQuery(giftListQuery);

  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);

  const transformedGiftList = useMemo(
    (): TransformedGiftItem[] =>
      (giftResponse?.data ?? []).map((giftItem) => {
        const status = getStatus(giftItem);
        const offer = getElementByUuid(giftItem.offer_plan.offer_uuid, offers);

        return {
          ...giftItem,
          offer_name: offer?.label,
          status,
          activationDate:
            status === "waiting" ? giftItem.limit_date : giftItem.start_date,
          expire_date: moment(giftItem.limit_date).format("DD/MM/YYYY"),
          begin_date: moment(giftItem.start_date).format("DD/MM/YYYY"),
          end_date: moment(giftItem.start_date)
            .add(giftItem.month_duration, "months")
            .format("DD/MM/YYYY"),
        };
      }),
    [giftResponse?.data, offers],
  );

  const handleResend = useCallback(
    (uuid: string): void => {
      dispatch(sendGift(uuid, token, projectSlug, t));
    },
    [dispatch, token, projectSlug, t],
  );

  const handleEdit = useCallback(
    (uuid: string): void => history.push(giftEditPath(projectSlug, uuid)),
    [history, projectSlug],
  );

  const columnList = useMemo(
    () =>
      giftColumns({
        t,
        canWrite: canAccessProject.giftsWrite,
        onResend: handleResend,
        onEdit: handleEdit,
      }),
    [t, canAccessProject.giftsWrite, handleResend, handleEdit],
  );

  const breadcrumbList = [
    { name: t("editor-menu:offerManagement"), url: "/gift" },
    { name: t("sectionTitleSimple") },
  ];

  const emptyState = (
    <DataTableEmptyState
      icon={GiftIcon}
      message={t("noGiftText")}
      title={t("sectionTitle")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbList} />
      <DataTable
        columns={columnList}
        data={transformedGiftList}
        defaultSorting={{ id: "updated_at", desc: true }}
        emptyState={emptyState}
        isError={isGiftsError}
        isLoading={isGiftsLoading}
        onRetry={refetchGifts}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default GiftList;
